<template>
  <!-- z above element-ui's dialogs/popovers - the agenda preview stacks its own at 2000+. -->
  <div v-if="visible" class="fixed inset-0 z-[3000] flex items-start justify-center overflow-y-auto p-4 pt-16">
    <button class="fixed inset-0 bg-slate-950/80 backdrop-blur-[1px]" aria-label="Close" @click="close"></button>

    <div class="relative w-full max-w-xl rounded-md bg-[#13191d] p-5 text-slate-200 shadow-2xl">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Update live input</p>
          <h3 class="mt-1 truncate text-lg font-semibold text-slate-100">{{ eventLabel }}</h3>
        </div>
        <button
          class="h-8 w-8 shrink-0 rounded border border-white/10 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
          aria-label="Close"
          @click="close"
        >
          <i class="el-icon-close"></i>
        </button>
      </div>

      <!-- Either a profile or the booking's own config goes on air - the API takes one or the other. -->
      <div class="mt-4 flex *:rounded-none first:*:rounded-l-xs last:*:rounded-r-xs">
        <button
          class="h-[30px] cursor-pointer border border-[#2e3b43] px-3 text-[11px]! font-medium transition-colors"
          :class="mode === 'profile' ? 'border-[#2f6f9f] bg-[#2f6f9f] text-[#eaf3fa]!' : 'bg-transparent text-[#cbd5da]! hover:bg-[#212a2f]'"
          @click="mode = 'profile'"
        >
          Input profile
        </button>
        <button
          class="-ml-px h-[30px] cursor-pointer border border-[#2e3b43] px-3 text-[11px]! font-medium transition-colors"
          :class="mode === 'config' ? 'border-[#2f6f9f] bg-[#2f6f9f] text-[#eaf3fa]!' : 'bg-transparent text-[#cbd5da]! hover:bg-[#212a2f]'"
          @click="mode = 'config'"
        >
          Event config
        </button>
      </div>

      <div v-if="mode === 'profile'" class="mt-4 space-y-2">
        <label class="block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Input profile</label>
        <select
          v-model="profileId"
          class="h-9! w-full! rounded-sm! border! border-[#2e3b43]! bg-[#13191d]! px-2! text-xs! text-slate-200! outline-none! focus:border-[#2f6f9f]!"
        >
          <option value="">Select a profile…</option>
          <option v-for="profile in profiles" :key="profile.id" :value="profile.id">
            {{ profile.name }}
          </option>
        </select>
        <p class="text-[11px] text-slate-500">
          Currently booked: {{ event && event.inputProfile ? event.inputProfile.name : 'no profile' }}
        </p>
      </div>

      <div v-else class="mt-4">
        <bly-input-profile-config-editor
          :value="configDraft"
          @input="configDraft = $event"
        ></bly-input-profile-config-editor>
      </div>

      <p class="mt-4 rounded-sm border border-[#f28c28]/40 bg-[#f28c28]/10 px-3 py-2 text-[11px] text-[#ffb763]">
        This reconfigures the ingest of an on-air event - the source will drop and reconnect.
      </p>

      <div class="mt-4 flex justify-end gap-2">
        <button
          class="h-[30px] cursor-pointer rounded-xs border border-[#2e3b43] bg-transparent px-3 text-[11px]! text-[#cbd5da]! transition-colors hover:bg-[#212a2f]"
          @click="close"
        >
          Cancel
        </button>
        <button
          class="h-[30px] cursor-pointer rounded-xs border border-[#2f8f52] bg-[#2f8f52] px-3 text-[11px]! font-medium text-[#eafaf0]! transition-colors hover:bg-[#37a660] disabled:cursor-default disabled:opacity-50"
          :disabled="saving || (mode === 'profile' && !profileId)"
          @click="apply"
        >
          {{ saving ? 'Applying…' : 'Apply live' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  DEFAULT_INPUT_PROFILE_CONFIG,
  parseInputProfileConfig,
} from '@/services/inputProfileConfig';

export default {
  name: 'LiveInputUpdate',

  props: {
    visible: { type: Boolean, default: false },
    event: { type: Object, default: null },
  },

  data() {
    return {
      mode: 'profile',
      profileId: '',
      configDraft: '',
      saving: false,
    };
  },

  computed: {
    ...mapState({
      inputProfiles: (state) => state.schedule.inputProfiles,
    }),

    profiles() {
      return [...(this.inputProfiles.items || [])]
        .filter(Boolean)
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    },

    eventLabel() {
      if (!this.event) return '';
      return this.event.fixture || this.event.name || this.event.id;
    },
  },

  watch: {
    visible(open) {
      if (open) this.reset();
    },
  },

  methods: {
    ...mapActions('routerAlt', ['eventUpdateInputLive']),
    ...mapActions('schedule', ['fetchInputProfiles']),

    reset() {
      const event = this.event || {};
      // A booking that already carries an override is most likely being tweaked, not swapped.
      this.mode = event.inputProfileOverride ? 'config' : 'profile';
      this.profileId = event.inputProfile?.id || '';
      const profileConfig = parseInputProfileConfig(event.inputProfile?.config);
      this.configDraft = JSON.stringify(
        event.inputProfileOverride
          || (Object.keys(profileConfig).length ? profileConfig : DEFAULT_INPUT_PROFILE_CONFIG),
      );
      this.saving = false;
      if (!(this.inputProfiles.items || []).length) this.fetchInputProfiles();
    },

    close() {
      this.$emit('update:visible', false);
    },

    async apply() {
      if (!this.event) return;
      const payload = { eventId: this.event.id };

      if (this.mode === 'profile') {
        if (!this.profileId) return;
        payload.inputProfileId = this.profileId;
      } else {
        try {
          JSON.parse(this.configDraft);
        } catch (error) {
          this.$message.error('Invalid input profile config');
          return;
        }
        // AWSJSON goes over the wire as a JSON string, which is what the editor emits.
        payload.inputProfileOverride = this.configDraft;
      }

      this.saving = true;
      try {
        const result = await this.eventUpdateInputLive(payload);
        if (result && !result.error) {
          this.$emit('updated');
          this.close();
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
